import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { SidebarLayout } from '@/layouts/sidebar-layout';
import { LoginPage } from '@/pages/auth/login-page';
import { DashboardPage } from '@/pages/dashboard/dashboard-page';
import { PosPage } from '@/pages/pos/pos-page';
import { ProductsListPage } from '@/pages/products/products-list';
import { ProductFormPage } from '@/pages/products/product-form';
import { CategoriesPage } from '@/pages/categories/categories-page';
import { BrandsPage } from '@/pages/categories/brands-page';
import { UnitsPage } from '@/pages/categories/units-page';
import { SalesHistoryPage } from '@/pages/sales/sales-history';
import { CustomersPage } from '@/pages/customers/customers-page';
import { CashRegisterPage } from '@/pages/cash-register/cash-register-page';
import { SuppliersListPage } from '@/pages/suppliers/suppliers-list';
import { SupplierFormPage } from '@/pages/suppliers/suppliers-form';
import { PurchasesListPage } from '@/pages/purchases/purchases-list';
import { PurchaseOrderFormPage } from '@/pages/purchases/purchases-form';
import { SupplierPaymentsPage } from '@/pages/payments/supplier-payments';
import { KardexPage } from '@/pages/inventory/kardex-page';
import { QuickEntryPage } from '@/pages/inventory/quick-entry-page';
import { TransfersPage } from '@/pages/inventory/transfers-page';
import { ReportsPage } from '@/pages/reports/reports-page';
import { UsersPage } from '@/pages/users/users-page';
import { RolesPage } from '@/pages/roles';
import { AuditPage } from '@/pages/audit/audit-page';
import { CompaniesPage } from '@/pages/companies/companies-page';
import { BranchesPage } from '@/pages/branches/branches-page';
import { SettingsPage } from '@/pages/settings/settings-page';
import { CreditNotesPage } from '@/pages/credit-notes/credit-notes-page';
import { PromotionsPage } from '@/pages/promotions/promotions-page';
import { InvoicesListPage } from '@/pages/invoices/invoices-list';
import { InvoiceFormPage } from '@/pages/invoices/invoice-form';
import { ReceivablesPage } from '@/pages/accounting/receivables-page';
import { PayablesPage } from '@/pages/accounting/payables-page';
import { ExpensesPage } from '@/pages/accounting/expenses-page';
import { TopNavLayout } from '@/layouts/top-nav-layout';
import { GlobalDialogProvider } from '@/components/GlobalDialogProvider';
import { BootScreen } from '@/components/BootScreen';
import { runBootSequence, BOOT_STEPS } from '@/services/boot.service';
import type { BootStepState } from '@/services/boot.service';

import { AutoSyncManager } from '@/components/AutoSyncManager';
import { SetupScreen } from '@/pages/auth/setup-screen';
import { useNetworkStore } from '@/stores/network-store';

import { NetworkSetupModal } from '@/components/setup/NetworkSetupModal';
import { PublicInvoicePage } from '@/pages/public-invoice/public-invoice-page';

export function UnconfiguredRoute({ children }: { children: React.ReactNode }) {
  const mode = useNetworkStore((state) => state.mode);
  if (mode === 'unconfigured') {
    return <Navigate to="/setup" replace />;
  }
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <AutoSyncManager />
      {children}
    </>
  );
}

function DynamicLayout() {
  const { user } = useAuthStore();
  const roleName = user?.role?.toLowerCase() || '';
  
  if (roleName.includes('vendedor') || roleName.includes('cajero')) {
    return <TopNavLayout />;
  }
  
  return <SidebarLayout />;
}

const BOOT_COMPLETED_KEY = 'pos_boot_completed';

export default function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [booting, setBooting] = useState(
    () => !localStorage.getItem(BOOT_COMPLETED_KEY),
  );
  
  // Novedad: Lógica de configuración de Red
  const [checkingNetwork, setCheckingNetwork] = useState(true);
  const [needsNetworkSetup, setNeedsNetworkSetup] = useState(false);

  const [bootSteps, setBootSteps] = useState<BootStepState[]>(
    BOOT_STEPS.map((s) => ({ ...s, status: 'pending' })),
  );
  const [bootProgress, setBootProgress] = useState(0);
  const [bootError, setBootError] = useState<string | null>(null);

  const updateStep = useCallback((stepId: string, status: BootStepState['status']) => {
    setBootSteps((prev) =>
      prev.map((s) => (s.id === stepId ? { ...s, status } : s)),
    );
  }, []);

  const startBoot = useCallback(async () => {
    setBootError(null);
    setBootProgress(0);
    setBootSteps(BOOT_STEPS.map((s) => ({ ...s, status: 'pending' })));

    const result = await runBootSequence(updateStep, setBootProgress);

    if (!result.success) {
      setBootError(result.error || 'Error de inicialización');
    } else {
      localStorage.setItem(BOOT_COMPLETED_KEY, 'true');
      await new Promise((r) => setTimeout(r, 300));
      setBooting(false);
    }
  }, [updateStep]);

  useEffect(() => {
    // 1. Verificamos si estamos en Electron y si ya hay una configuración de red
    const checkSettings = async () => {
      const isElectron = navigator.userAgent.toLowerCase().includes('electron');
      if (isElectron && (window as any).electronAPI && (window as any).electronAPI.getSettings) {
        try {
          const settings = await (window as any).electronAPI.getSettings();
          if (!settings.isConfigured) {
            setNeedsNetworkSetup(true);
            setCheckingNetwork(false);
            return;
          }
        } catch (err) {
          console.error("Error reading settings", err);
        }
      }
      setCheckingNetwork(false);
    };
    checkSettings();
  }, []);

  useEffect(() => {
    if (!checkingNetwork && !needsNetworkSetup && booting) {
      startBoot();
    }
  }, [checkingNetwork, needsNetworkSetup, booting, startBoot]);

  if (checkingNetwork) {
    return null; // Podría ser un loader, pero es instantáneo
  }

  if (needsNetworkSetup) {
    return (
      <NetworkSetupModal 
        onComplete={() => {
          setNeedsNetworkSetup(false);
          setBooting(true);
        }} 
      />
    );
  }

  // ── Show boot screen while initializing ──
  if (booting) {
    return (
      <BootScreen
        steps={bootSteps}
        progress={bootProgress}
        error={bootError}
        onRetry={startBoot}
      />
    );
  }

  // ── Normal app ──
  return (
    <>
      <GlobalDialogProvider />
      <Routes>
        <Route path="/setup" element={<SetupScreen />} />
        <Route path="/facturacion" element={<PublicInvoicePage />} />
        <Route
          path="/login"
          element={
            <UnconfiguredRoute>
              {isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
            </UnconfiguredRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DynamicLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="pos" element={<PosPage />} />
          <Route path="products" element={<ProductsListPage />} />
          <Route path="products/new" element={<ProductFormPage />} />
          <Route path="products/:id" element={<ProductFormPage />} />
          <Route path="products/:id/edit" element={<ProductFormPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="brands" element={<BrandsPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="sales" element={<SalesHistoryPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="cash-register" element={<CashRegisterPage />} />
          <Route path="suppliers" element={<SuppliersListPage />} />
          <Route path="suppliers/new" element={<SupplierFormPage />} />
          <Route path="suppliers/:id" element={<SupplierFormPage />} />
          <Route path="purchases" element={<PurchasesListPage />} />
          <Route path="purchases/new" element={<PurchaseOrderFormPage />} />
          <Route path="purchases/:id" element={<PurchaseOrderFormPage />} />
          <Route path="supplier-payments" element={<SupplierPaymentsPage />} />
          <Route path="inventory/quick-entry" element={<QuickEntryPage />} />
          <Route path="inventory/transfers" element={<TransfersPage />} />
          <Route path="kardex" element={<KardexPage />} />
          <Route path="kardex/:productId" element={<KardexPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="promotions" element={<PromotionsPage />} />
          <Route path="credit-notes" element={<CreditNotesPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="companies" element={<CompaniesPage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="invoices" element={<InvoicesListPage />} />
          <Route path="invoices/new" element={<InvoiceFormPage />} />
          <Route path="accounting/receivables" element={<ReceivablesPage />} />
          <Route path="accounting/payables" element={<PayablesPage />} />
          <Route path="accounting/expenses" element={<ExpensesPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
